import { useEffect, useRef } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { getHanko } from '@/auth/hanko';
import { runtimeConfig } from '@/runtime-config';
import { useLocale, useT } from '@/i18n/locale';
import tile from '@/assets/clockoo-tile-light.svg';

export const Route = createFileRoute('/login')({
  component: LoginPage,
});

const HANKO_API_URL = runtimeConfig.hankoApiUrl;

function LoginPage() {
  const t = useT();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current || !HANKO_API_URL) return;
    // Built imperatively (not JSX) — <hanko-auth> is a custom element registered globally by
    // initHanko() (main.tsx); JSX would need a global TS declaration for the tag that the
    // hanko-elements package doesn't ship. Per the Hanko docs the display language is read
    // from the element's `lang` attribute, not `<html lang>`, so this re-runs on locale change.
    const element = document.createElement('hanko-auth');
    element.setAttribute('api', HANKO_API_URL);
    element.setAttribute('lang', locale);
    ref.current.replaceChildren(element);

    // hanko-elements dispatches via the Hanko client, not a DOM event — onSessionCreated fires
    // the moment the session cookie has been written.
    const hanko = getHanko();
    const unsubscribe = hanko.onSessionCreated(() => void navigate({ to: '/instances' }));
    return () => unsubscribe();
  }, [locale, navigate]);

  return (
    <div className="flex min-h-full items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm rounded-2xl border border-[#e3e3e9] bg-white p-7 shadow-[0_20px_50px_rgba(18,18,30,0.16)]">
        <div className="flex flex-col items-center text-center">
          <img src={tile} alt="Clockoo" className="size-14 rounded-2xl shadow-[0_8px_20px_rgba(44,48,104,0.28)]" />
          <h1 className="mt-4 text-lg font-semibold tracking-[-0.3px] text-[#111114]">{t('login.title')}</h1>
          <p className="mt-1 text-sm text-[#83838c]">{t('login.subtitle')}</p>
        </div>

        <div ref={ref} className="mt-6" />
        {!HANKO_API_URL && (
          <p className="mt-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">{t('login.hankoMissing')}</p>
        )}

        <p className="mt-6 text-center text-xs text-[#a4a4ad]">{t('login.brandedBy')}</p>
      </div>
    </div>
  );
}
